import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    MdSearch,
    MdFileDownload,
    MdFilterList
} from 'react-icons/md';
import { formatDate } from '../utils/dateUtils';
import { exportToCSV } from '../utils/exportUtils';
import Loader from '../components/common/Loader';
import NoData from '../components/common/NoData';
import Pagination from '../components/common/Pagination';
import { authFetch } from '../components/utils/authFetch';
import { GET_PROJECT_EXPENSE_REPORT_API, GET_ALL_VENDORS_API, GET_ALL_POS_API } from '../config/API';
import Drawer from '../components/common/Drawer';
import PaginatedSelect from '../components/common/PaginatedSelect';
import { toast } from 'react-toastify';

import styles from './ProjectExpenseReport.module.css';

const ProjectExpenseReport = () => {
    const navigate = useNavigate();

    const [reports, setReports] = useState([]);
    const [loading, setLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalRecords, setTotalRecords] = useState(0);
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [expenses, setExpenses] = useState([]);

    const [showFilters, setShowFilters] = useState(false);
    const [dateRange, setDateRange] = useState({ start: '', end: '' });
    const [selectedVendor, setSelectedVendor] = useState(null);
    const [selectedPo, setSelectedPo] = useState(null);


    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(searchTerm);
        }, 500);
        return () => clearTimeout(timer);
    }, [searchTerm]);

    const fetchReports = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('accessToken');
            let queryParams = `?page=${currentPage}&limit=10&search=${debouncedSearch}`;

            if (dateRange.start) queryParams += `&startDate=${dateRange.start}`;
            if (dateRange.end) queryParams += `&endDate=${dateRange.end}`;
            if (selectedVendor?.label) queryParams += `&vendorName=${selectedVendor.label}`;
            if (selectedPo?.label) queryParams += `&poNumber=${selectedPo.label}`;

            const response = await authFetch(
                `${GET_PROJECT_EXPENSE_REPORT_API}${queryParams}`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );
            const result = await response.json();

            if (result.status) {
                setExpenses(result.data || []);
                const total = result.total || result.pagination?.total || result.pagination?.totalRecords || result.count || 0;
                setTotalPages(result.pagination?.totalPages || Math.ceil(total / 10) || 1);
                setTotalRecords(total);
            }
        } catch (error) {
            console.error('Error fetching project expense report:', error);
            setExpenses([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchReports();
    }, [currentPage, debouncedSearch, dateRange, selectedVendor, selectedPo]);

    const handleExport = async () => {
        try {
            const token = localStorage.getItem('accessToken');
            let queryParams = `?exportCSV=true&search=${debouncedSearch}`;

            if (dateRange.start) queryParams += `&startDate=${dateRange.start}`;
            if (dateRange.end) queryParams += `&endDate=${dateRange.end}`;
            if (selectedVendor?.label) queryParams += `&vendorName=${selectedVendor.label}`;
            if (selectedPo?.label) queryParams += `&poNumber=${selectedPo.label}`;

            const response = await authFetch(
                `${GET_PROJECT_EXPENSE_REPORT_API}${queryParams}`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );
            const result = await response.json();

            if (result.status) {
                toast.success(result.message || 'Export started! Check Report Log for the file.');
            } else {
                toast.error(result.message || 'Failed to start export');
            }
        } catch (error) {
            console.error('Error exporting project expense report:', error);
            toast.error('An error occurred during export');
        }
    };


    return (
        <div className={styles['purchase-orders-container']}>
            <div className={styles['purchase-orders-header']}>
                <h2>Project Expense Report</h2>
                <div className={styles['header-actions']}>
                    <button
                        className={`${styles['filter-btn']} ${showFilters ? styles.active : ''}`}
                        onClick={() => setShowFilters(!showFilters)}
                    >
                        <MdFilterList /> Filter
                    </button>
                    <button
                        className={styles['export-btn']}
                        onClick={handleExport}
                    >
                        <MdFileDownload /> Export
                    </button>
                </div>
            </div>

            <Drawer
                isOpen={showFilters}
                onClose={() => setShowFilters(false)}
                title="Filter Reports"
            >
                <div className={styles['drawer-filter-content']}>
                    <div className={styles['filter-item']}>
                        <label>Vendor</label>
                        <PaginatedSelect
                            apiUrl={GET_ALL_VENDORS_API}
                            mapResponse={(v) => ({
                                value: v.id,
                                label: v.vendorName || v.companyName
                            })}
                            value={selectedVendor}
                            onChange={(option) => {
                                setSelectedVendor(option);
                                setSelectedPo(null);
                            }}
                            placeholder="Select Vendor"
                            isClearable
                        />
                    </div>
                    <div className={styles['filter-item']}>
                        <label>PO Number</label>
                        <PaginatedSelect
                            apiUrl={GET_ALL_POS_API}
                            mapResponse={(po) => ({
                                value: po.id,
                                label: po.poNumber
                            })}
                            value={selectedPo}
                            onChange={(option) => setSelectedPo(option)}
                            placeholder="Search PO Number"
                            isClearable
                            params={{ vendorId: selectedVendor?.value }}
                        />
                    </div>
                    <div className={styles['filter-item']}>
                        <label>Start Date</label>
                        <input
                            type="date"
                            value={dateRange.start}
                            onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
                        />
                    </div>
                    <div className={styles['filter-item']}>
                        <label>End Date</label>
                        <input
                            type="date"
                            value={dateRange.end}
                            onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
                        />
                    </div>
                    <div className={styles['drawer-actions']}>
                        <button
                            className={styles['clear-filters-btn']}
                            onClick={() => {
                                setDateRange({ start: '', end: '' });
                                setSelectedVendor(null);
                                setSelectedPo(null);
                            }}
                            style={{ width: '100%' }}
                        >
                            Reset All Filters
                        </button>
                    </div>
                </div>
            </Drawer>

            <div className={styles['card-container-po']}>

                {/* Search Bar */}
                <div className={styles['search-container']}>
                    <div className={styles['search-wrapper']}>
                        <MdSearch className={styles['search-icon']} />
                        <input
                            type="text"
                            placeholder="Search by PO No or Vendor..."
                            className={styles['search-input']}
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                        {searchTerm && (
                            <button
                                className={styles['clear-search-btn']}
                                onClick={() => setSearchTerm('')}
                                title="Clear search"
                            >
                                ✕
                            </button>
                        )}
                    </div>
                </div>

                <div className={styles['table-container']}>
                    <table>
                        <thead>
                            <tr>
                                <th>Sn</th>
                                <th>PO Number</th>
                                <th> PO Created Date</th>
                                <th>Vendor Name</th>
                                <th>Amount</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr>
                                    <td colSpan="5" style={{ textAlign: 'center', padding: '3rem' }}>
                                        <Loader text="Fetching expense data..." />
                                    </td>
                                </tr>
                            ) : expenses.length === 0 ? (
                                <tr>
                                    <td colSpan="5" style={{ textAlign: 'center', padding: '3rem' }}>
                                        <NoData title="No Expenses Found" message="Try adjusting your search filters." />
                                    </td>
                                </tr>
                            ) : (
                                expenses.map((exp, index) => {
                                    const expenseAmount = (exp.Receipts || []).reduce((sum, r) => sum + parseFloat(r.amount || 0), 0);
                                    const vendorName = exp.RequestVendor?.Vendor?.vendorName || 'N/A';
                                    return (
                                        <tr key={exp.id}>
                                            <td>{(currentPage - 1) * 10 + index + 1}</td>
                                            <td>
                                                <span className={styles['po-number']}>#{exp.poNumber}</span>
                                            </td>
                                            <td>{formatDate(exp.date)}</td>
                                            <td>{vendorName}</td>
                                            <td className={styles['amount-cell']}>₹{expenseAmount.toLocaleString()}</td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>

                <div className={styles['mobile-card-view']}>
                    {loading ? (
                        <div style={{ padding: '2rem', textAlign: 'center' }}>
                            <Loader text="Fetching expense data..." />
                        </div>
                    ) : expenses.length === 0 ? (
                        <div style={{ padding: '2rem', textAlign: 'center' }}>
                            <NoData title="No Expenses Found" message="Try adjusting your search filters." />
                        </div>
                    ) : (
                        expenses.map((exp, index) => {
                            const expenseAmount = (exp.Receipts || []).reduce((sum, r) => sum + parseFloat(r.amount || 0), 0);
                            const vendorName = exp.RequestVendor?.Vendor?.vendorName || 'N/A';
                            return (
                                <div key={exp.id} className={styles['dashboard-card']}>
                                    <div className={styles['card-header']}>
                                        <div className={styles['card-title']}>
                                            <div className={styles['card-sn']}>{(currentPage - 1) * 10 + index + 1}</div>
                                            <strong>{formatDate(exp.date)}</strong>
                                            <span className={`${styles['status-badge']} ${styles['status-closed']} ${styles['status-badge-receipt']}`}>
                                                #{exp.poNumber}
                                            </span>
                                        </div>
                                    </div>
                                    <div className={styles['card-body']}>
                                        <div className={styles['card-row']}>
                                            <span className={styles['card-label']}>Vendor</span>
                                            <span className={styles['card-value']}><strong>{vendorName}</strong></span>
                                        </div>
                                        <div className={styles['card-row']}>
                                            <span className={styles['card-label']}>Amount</span>
                                            <span className={`${styles['card-value']} ${styles['amount-cell']}`}>₹{expenseAmount.toLocaleString()}</span>
                                        </div>
                                    </div>
                                </div>
                            );
                        })
                    )}
                </div>

                <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    totalRecords={totalRecords}
                    onPageChange={(page) => setCurrentPage(page)}
                    currentCount={expenses.length}
                    entityName="Project Expense Report"
                />
            </div>
        </div>
    );
};

export default ProjectExpenseReport;
